import React from 'react';
import { VStack, HStack, Text, Heading, Box, Icon } from '@chakra-ui/react';
import { LucideIcon } from 'lucide-react';

interface DetailItem {
  label: string;
  value: string | number | null | undefined;
  render?: (value: string | number | null | undefined) => React.ReactNode;
  stacked?: boolean;
}

interface DetailsSectionProps {
  title: string;
  icon: LucideIcon;
  items: DetailItem[];
  hideEmptyFields?: boolean;
}

const DetailsSection: React.FC<DetailsSectionProps> = ({ title, icon, items, hideEmptyFields = true }) => {
  // Filter out items that have falsy values (null, undefined, empty string)
  const visibleItems = hideEmptyFields ? items.filter(item => {
    const value = item.value;
    return value !== null && value !== undefined && value !== '';
  }) : items;
  
  // Don't render the section if no items are visible
  if (visibleItems.length === 0) {
    return null;
  }

  const renderValue = (item: DetailItem) => {
    if (item.render) {
      return item.render(item.value);
    }
    
    // Default text renderer
    return <Text>{String(item.value)}</Text>;
  };

  return (
    <Box 
      p={4} 
      borderWidth={1} 
      borderRadius="md" 
      borderColor="gray.200" 
      bg="white"
      _dark={{ 
        borderColor: "gray.600", 
        bg: "gray.700" 
      }}
    >
      <VStack align="start" gap={4}>
        <HStack>
          <Icon as={icon} boxSize={4} color="brand.500" />
          <Heading size="sm" color="brand.600" _dark={{ color: "brand.300" }}>
            {title}
          </Heading>
        </HStack>
        
        <VStack gap={3} align="stretch" w="full">
          {visibleItems.map((item, index) => (
            item.stacked ? (
              <VStack key={index} align="stretch" gap={1}>
                <Text fontSize="sm" fontWeight="medium" color="gray.600" _dark={{ color: "gray.400" }}>
                  {item.label}
                </Text>
                <Box textAlign="left" w="full">
                  {renderValue(item)}
                </Box>
              </VStack>
            ) : (
              <HStack key={index} justify="space-between" align="start">
                <Text fontSize="sm" fontWeight="medium" color="gray.600" _dark={{ color: "gray.400" }}>
                  {item.label}
                </Text>
                <Box textAlign="right">
                  {renderValue(item)}
                </Box>
              </HStack>
            )
          ))}
        </VStack>
      </VStack>
    </Box>
  );
};

export default DetailsSection; 
